/**
 * useDownloadBoardPdf -- mutation that fetches the board PDF
 * endpoint as a Blob + triggers a browser download
 * (P17.3 / #458).
 *
 * Same posture as `useDownloadDossier`: bypasses `api.request`
 * (which JSON-parses every body) so we can stream the
 * application/pdf bytes raw. Token is attached manually from the
 * same `_tokenGetter` source the rest of the SPA reads from.
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { currentAuthToken } from "shared/api/client";

export interface BoardPdfDownloadInput {
  /** ISO 8601 with tz offset. Replays chain state at this instant. */
  as_of?: string | undefined;
}

export interface BoardPdfDownloadResult {
  filename: string;
}

export class BoardPdfDownloadError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
    this.name = "BoardPdfDownloadError";
  }
}

function _buildQueryString(input: BoardPdfDownloadInput): string {
  if (!input.as_of) return "";
  return `?as_of=${encodeURIComponent(input.as_of)}`;
}

function _filenameFromContentDisposition(header: string | null): string {
  if (!header) return "board-snapshot.pdf";
  const match = header.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? "board-snapshot.pdf";
}

async function _triggerDownload(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

async function _downloadBoardPdf(
  input: BoardPdfDownloadInput,
): Promise<BoardPdfDownloadResult> {
  const token = currentAuthToken();
  const response = await fetch(`/v1/board/snapshot.pdf${_buildQueryString(input)}`, {
    method: "GET",
    headers: {
      Accept: "application/pdf",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      const body = await response.json();
      detail = body?.detail ?? detail;
    } catch {
      // text body or empty; keep statusText
    }
    throw new BoardPdfDownloadError(response.status, detail);
  }
  const filename = _filenameFromContentDisposition(
    response.headers.get("Content-Disposition"),
  );
  const blob = await response.blob();
  await _triggerDownload(blob, filename);
  return { filename };
}

export function useDownloadBoardPdf(): UseMutationResult<
  BoardPdfDownloadResult,
  BoardPdfDownloadError,
  BoardPdfDownloadInput
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: _downloadBoardPdf,
  });
}
